import {
    Component,
    Input,
    ContentChild,
    ViewChild,
    OnInit,
    OnDestroy,
    ChangeDetectorRef,
    HostListener
} from '@angular/core';
import { MatFormFieldControl, MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';

/**
 * This component encapsulates the form field with Tyler styling.
 * This component uses content projection and accepts the following elements between its tags:
 *      matInput
 *      <mat-select>
 *      matSuffix
 *      matPrefix
 *
 * @input color: the color theme for the field. Defaults to 'primary'.
 * @input floatLabel: whether the label should float. Defaults to 'auto'.
 * @input hideRequiredMarker: whether the required * should be hidden. Defaults to false.
 * @input hint: the hint for the field.
 * @input hintAlign: where the hint should be aligned. Defaults to 'end'.
 * @input hintColor: the color of the hint text. Defaults to null.
 * @input label: the label for the field. Defaults to null.
 * @input errorMessage: the error message that displays when the control is invalidated. Defaults to 'You must enter a valid value'.
 * @input fullWidth: sets the width of the mat-form-field to 100%. Defaults to false.
 * @input clearable: if true, allows the input to be cleared by clicking on an X while hovering
 * @input preventCollapse: if true, prevents the space from collapsing to its smallest possible spacing. Defaults to false.
 *
 * NOTES:
 *
 * When you're using errors, use can the control (matInput or <mat-select>) to determine when the error should show.
 * For more information, look at the Angular documentation for Material Input and ErrorStateMatcher
 *
 * You CANNOT use <mat-hint> and <mat-label> between the tags because MatFormField does not natively support content projection.
 * As such, you MUST use the label and hint variables to achieve the same effect.
 */
@AutoUnsubscribe()
@Component({
    selector: 'tyl-form-field',
    templateUrl: './tyl-form-field.component.html',
    styleUrls: [
        './tyl-form-field.component.css'
    ]
})
export class TylFormFieldComponent implements OnInit, OnDestroy {

    // Interface Properties for MatFormField
    @Input() floatLabel = 'auto';
    @Input() hideRequiredMarker = false;
    @Input() hintLabel: string;
    @Input() hintColor = '';
    @Input() clearable = false;
    @Input() preventCollapse = false;
    @Input() required = false;
    @Input() showErrorMessage = true;
    @Input() postLabelSpace = false;

    // Custom Interface Properties
    @Input() label: string;
    // @Input() showError: boolean;
    @Input() fullWidth = false;

    // Local Properites
    private _color = 'primary';
    private _control: MatFormFieldControl<any>;
    private _matFormField: MatFormField;
    private _hint: string;
    private _hintAlign = 'end';
    private _errorMessage = 'Required field';
    private _showError: boolean;
    hovering = false;
    marginBottom = '0px';

    // Class Functions
    constructor(
        private changeDetector: ChangeDetectorRef
    ) { }

    get hint(): string {
        return this._hint;
    }

    @Input() set hint(value: string) {
        this._hint = value;
        this.determineMarginBottom();

        // Preventing ExpressionChangedAfterItHasBeenCheckedError
        // this.changeDetector.detectChanges();
    }

    get hintAlign() {
        return this._hintAlign;
    }

    @Input() set hintAlign(value: string) {
        this._hintAlign = value;

        // Preventing ExpressionChangedAfterItHasBeenCheckedError
        // this.changeDetector.detectChanges();
    }

    get errorMessage(): string {
        return this._errorMessage;
    }

    @Input() set errorMessage(value: string) {
        this._errorMessage = value;
        this.determineMarginBottom();
    }

    get showError(): boolean {
        return this._showError;
    }

    @Input() set showError(value: boolean) {
        this._showError = value;
        this.determineMarginBottom();
    }

    get control() {
        return this._control;
    }

    @ContentChild(MatFormFieldControl) set control(value: MatFormFieldControl<any>) {
        this._control = value;
        if (this._control != null) {
            if (this.matFormField) {
                this.matFormField._control = this._control;
            }
            if (this._control instanceof MatSelect) {
                this._control.panelClass = 'mat-' + this.color;
            }
            this._control.stateChanges.subscribe(() => {
                this.determineMarginBottom();
                this.matFormField.updateOutlineGap();
            });
            this.determineMarginBottom();
        }
    }

    get matFormField() {
        return this._matFormField;
    }

    @ViewChild(MatFormField, { static: true }) set matFormField(value: MatFormField) {
        this._matFormField = value;
        if (this.control) {
            this._matFormField._control = this.control;
        }
    }

    get color(): string {
        return this._color;
    }

    @Input() set color(value: string) {
        this._color = value;
        if (this._control instanceof MatSelect) {
            this._control.panelClass = 'mat-' + this.color;
        }
    }

    // Event Functions
    ngOnInit() {
        // POST: sets the form field's control to the control
        this.matFormField._control = this.control;
        if (!this.label) {
            // Reduce space more
        }
    }

    ngOnDestroy() {
        // NOTE: this is implemented for @AutoUnsubscribe
    }

    clear() {
        // PRE: the red X is clicked to clear the input
        // POST: clears out the input
        this.control.value = '';
        if (this.control.ngControl) {
            this.control.ngControl.viewToModelUpdate('');
        }
    }

    @HostListener('mouseenter', ['$event'])
    mouseEnter(event) {
        // PRE: the mouse enters this component
        // POST: sets hovering to true to allow showing the red X
        this.hovering = true;
        event.stopPropagation();
    }

    @HostListener('mouseleave')
    mouseLeave() {
        // PRE: the mouse leaves the component
        // POST: sets hovering to false to disable showing the red X
        this.hovering = false;
    }



    // Methods
    determineMarginBottom() {
        // PRE: one of the fields checked here is changed
        // POST: determines what the margin bottom ought to be
        if (!this.preventCollapse && // If we're not forcing the space
            !this.hint &&   // If there is no hint
            (
                !this.errorMessage || // And no error message
                (
                    this.control && // Or the control's validator is null and it isn't required
                    (
                        !this.control.ngControl || !this.control.ngControl.validator
                    ) &&
                    !this.control.required
                )
            )
        ) {
            this.marginBottom = '-0.84375em';   // Shorten the bottom
        } else {
            this.marginBottom = '0px'; // Otherwise, Keep the bottom length
        }
    }
}
